
import { useState, useEffect } from 'react';
import i18n from '@/i18n/config';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ProfileFormData, SettingsFormData, BreederApplicationData } from '@/types/profile';
import { createBreeder, getBreederByUserId } from '@/services/breederService';
import { supabase } from '@/integrations/supabase/client';
import { BreederVerificationDocs } from '@/types/supabase-views';
import { logger } from '@/utils/logger';

export function useProfileState() {
  const navigate = useNavigate();
  const [isProfileDialogOpen, setIsProfileDialogOpen] = useState(false);
  const [isSettingsDialogOpen, setIsSettingsDialogOpen] = useState(false);
  const [isBreederApplicationDialogOpen, setIsBreederApplicationDialogOpen] = useState(false);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);

  const [userProfile, setUserProfile] = useState({
    name: '',
    email: '',
    age: '' as string | number,
    address: '',
    phone: ''
  });

  const [userSettings, setUserSettings] = useState({
    notificationPreferences: ["email", "app"],
    language: "fr",
    isBreeder: false
  });

  const profileForm = useForm<ProfileFormData>({
    defaultValues: {
      name: '',
      email: '',
      age: '',
      address: '',
      phone: ''
    }
  });

  const settingsForm = useForm<SettingsFormData>({
    defaultValues: {
      notificationPreferences: userSettings.notificationPreferences.join(', '),
      language: userSettings.language,
      isBreeder: userSettings.isBreeder
    }
  });

  // Charger le profil réel depuis Supabase au montage
  useEffect(() => {
    const loadProfile = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const user = session?.user;
        if (!user) {
          setIsLoadingProfile(false);
          return;
        }

        const meta = user.user_metadata || {};
        const profile = {
          name: meta.full_name || meta.name || '',
          email: user.email || '',
          age: meta.age || '',
          address: meta.address || '',
          phone: meta.phone || '',
        };

        setUserProfile(profile);
        profileForm.reset({
          name: profile.name,
          email: profile.email,
          age: String(profile.age),
          address: profile.address,
          phone: profile.phone,
        });
      } catch (error) {
        logger.error('Error loading profile:', error);
      } finally {
        setIsLoadingProfile(false);
      }
    };

    loadProfile();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sauvegarder le profil dans Supabase (user_metadata + table profiles)
  const onSubmitProfile = async (values: ProfileFormData) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) {
        toast.error('Vous devez être connecté');
        return;
      }

      // Sauvegarder dans user_metadata (email, name, phone, address, age)
      const { error: authError } = await supabase.auth.updateUser({
        data: {
          full_name: values.name,
          name: values.name,
          age: values.age,
          address: values.address,
          phone: values.phone,
        }
      });

      if (authError) throw authError;

      // Mettre à jour first_name / last_name dans la table profiles
      const nameParts = (values.name || '').trim().split(/\s+/);
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      await supabase.from('profiles').upsert(
        { user_id: user.id, first_name: firstName, last_name: lastName },
        { onConflict: 'user_id' }
      );

      setUserProfile({
        ...values,
        age: values.age,
      });

      toast.success('Profil mis à jour avec succès');
      setIsProfileDialogOpen(false);
    } catch (error: unknown) {
      logger.error('Error saving profile:', error);
      toast.error('Erreur lors de la sauvegarde du profil');
    }
  };

  const onSubmitSettings = (values: SettingsFormData) => {
    const updatedSettings = {
      ...userSettings,
      notificationPreferences: values.notificationPreferences.split(',').map(pref => pref.trim()),
      language: values.language,
      isBreeder: values.isBreeder
    };

    setUserSettings(updatedSettings);
    if (values.language && values.language !== i18n.language) {
      i18n.changeLanguage(values.language);
    }
    toast.success("Paramètres mis à jour avec succès");
    setIsSettingsDialogOpen(false);
  };

  const onSubmitBreederApplication = async (values: BreederApplicationData & { idCard?: FileList; proofOfActivity?: FileList; galleryPhotos?: File[] }) => {
    try {
      const { data: { session: _authSession } } = await supabase.auth.getSession();
      const auth = { user: _authSession?.user ?? null };
      const uid = auth?.user?.id;
      if (!uid) {
        toast.error("Veuillez vous connecter pour continuer");
        return;
      }
      // Vérifier si l'utilisateur a déjà un profil d'éleveur
      const existingBreeder = await getBreederByUserId(uid);
      if (existingBreeder) {
        toast.error("Vous avez déjà un profil d'éleveur");
        return;
      }

      // Téléverser les documents de vérification si fournis
      let idCardPath: string | undefined;
      let proofPath: string | undefined;
      try {
        if (values.idCard && values.idCard[0]) {
          const file = values.idCard[0];
          const path = `${uid}/idcard-${Date.now()}-${file.name}`;
          const { error } = await supabase.storage.from('breeder-verifications').upload(path, file, { upsert: true });
          if (error) throw error;
          idCardPath = path;
        }
        if (values.proofOfActivity && values.proofOfActivity[0]) {
          const file = values.proofOfActivity[0];
          const path = `${uid}/proof-${Date.now()}-${file.name}`;
          const { error } = await supabase.storage.from('breeder-verifications').upload(path, file, { upsert: true });
          if (error) throw error;
          proofPath = path;
        }
      } catch (e) {
        logger.error('Upload documents failed', e);
        toast.error("Échec du téléversement des documents");
      }

      // Créer un nouveau profil d'éleveur
      const breeder = await createBreeder({
        name: values.breederName || `Élevage de ${(userProfile.name || 'utilisateur').split(' ')[0]}`,
        description: values.breederDescription || "Éleveur passionné avec une attention particulière au bien-être animal et à la qualité génétique.",
        specializations: values.specializations || ["Chiens", "Chats"],
        certifications: values.certifications || [],
        years_experience: values.experience || 1,
        show_email: true,
        show_phone: true,
        email: userProfile.email,
        phone: userProfile.phone || "",
        address: userProfile.address,
        city: values.city || "",
        region: values.region || "",
        postal_code: values.postalCode || "",
        siret: values.siret || undefined,
        verification_status: 'pending',
        verification_docs: { id_card_path: idCardPath, proof_path: proofPath } as BreederVerificationDocs,
        is_active: true,
        is_verified: false,
        is_premium: false,
        is_featured: false,
        total_views: 0,
        total_contacts: 0,
        special_tags: [],
        gallery_urls: [],
        user_id: uid
      });

      // Upload des photos de galerie après création du breeder
      if (breeder?.id && values.galleryPhotos && values.galleryPhotos.length > 0) {
        try {
          const galleryUrls: string[] = [];

          for (const file of values.galleryPhotos) {
            const fileExt = file.name.split('.').pop();
            const fileName = `gallery/${breeder.id}/${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;

            const { error: uploadError } = await supabase.storage
              .from('breeder-photos')
              .upload(fileName, file, { cacheControl: '3600', upsert: true });

            if (uploadError) {
              logger.error('Upload error:', uploadError);
              continue;
            }

            const { data: urlData } = supabase.storage
              .from('breeder-photos')
              .getPublicUrl(fileName);

            galleryUrls.push(urlData.publicUrl);
          }

          if (galleryUrls.length > 0) {
            await supabase
              .from('breeders')
              .update({ gallery_urls: galleryUrls })
              .eq('id', breeder.id);
          }
        } catch (error) {
          logger.error('Erreur lors de l\'upload des photos de galerie:', error);
        }
      }

      const updatedSettings = {
        ...userSettings,
        isBreeder: true
      };

      setUserSettings(updatedSettings);
      toast.success("Votre profil éleveur a été créé avec succès !");
      setIsBreederApplicationDialogOpen(false);

      if (breeder?.id) {
        setTimeout(() => {
          navigate(`/breeders/${breeder.id}`);
        }, 1000);
      }
    } catch (error) {
      logger.error("Erreur lors de la création du profil d'éleveur:", error);
      toast.error("Une erreur est survenue lors de la création de votre profil d'éleveur");
    }
  };

  return {
    isProfileDialogOpen,
    setIsProfileDialogOpen,
    isSettingsDialogOpen,
    setIsSettingsDialogOpen,
    isBreederApplicationDialogOpen,
    setIsBreederApplicationDialogOpen,
    isLoadingProfile,
    userProfile,
    userSettings,
    profileForm,
    settingsForm,
    onSubmitProfile,
    onSubmitSettings,
    onSubmitBreederApplication
  };
}
